import { Line } from "@ant-design/charts";
import { UploadOutlined } from '@ant-design/icons';
import { API } from "@aws-amplify/api";
import { Button, Descriptions, Empty, Spin, Table, Tag, Upload, notification } from "antd";
import combineQuery from 'graphql-combine-query';
import gql from 'graphql-tag';
import { print } from 'graphql/language/printer';
import moment from "moment-timezone";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { VList } from "virtuallist-antd";
import {
  createHealthRecord
} from "../backend/graphql/mutations";
import {
  listHealthRecords,
  listHealthRecordsByDate,
} from "../backend/graphql/queries";
import { AppContext } from "../contexts/AppContext";

const HealthRecords = () => {
  const { actionSetPageTitle } = useContext(AppContext);
  const { accountID } = useParams();
  const { state } = useLocation();
  const { user, viewChart } = state || { user: null, viewChart: false };
  const [values, setValues] = useState({
    sites: [0],
  });
  const [table, setTable] = useState({
    data: -1,
    loading: false,
    nextToken: null,
  });
  const [chartData, setChartData] = useState([]);
  const [displayList, setDisplayList] = useState(
    // default []
    // ["dummy data"]
    []
  );
  const [uploadedObj, setUploadedObj] = useState()

  const getHealthRecords = useCallback(async (nextToken, _values) => {
    setTable({
      ...table,
      loading: true,
    });

    try {
      let data = {};

      // filter by accountID
      if (accountID) {
        const variables = {
          accountID: accountID,
          sortDirection: "DESC",
        };
        if (nextToken) {
          variables.nextToken = nextToken;
        }
        // console.log("variables", variables);

        const result = await API.graphql({
          query: listHealthRecordsByDate,
          variables,
          authMode: "AMAZON_COGNITO_USER_POOLS",
        });
        // console.log("result", result);
        data = result.data.result;
      } else {
        const variables = {
          // filter: filter,
        };
        if (nextToken) {
          variables.nextToken = nextToken;
        }
        // console.log("variables", variables);

        const result = await API.graphql({
          query: listHealthRecords,
          variables,
          authMode: "AMAZON_COGNITO_USER_POOLS",
        });
        // console.log("result", result);
        data = result.data.result;
      }
      // console.log("data", data);
      setTable({
        ...table,
        data: nextToken ? table.data.concat(data.items) : data.items,
        loading: data.items.length === 0 ? false : true,
        nextToken: data.nextToken,
      });
    } catch (error) {
      console.error("error:", error);
      notification.error({
        message: "Unable to retrieve deployment",
      });
      setTable({
        ...table,
        loading: false,
      });
    }
  }, [table.nextToken, accountID]);

  useEffect(() => {
    actionSetPageTitle("Health Records");

    const currentDatetime = moment();
    let _values = {
      ...values,
      date: currentDatetime,
    };
    setValues(_values);
    getHealthRecords(table.nextToken, _values);

    return () => {
      actionSetPageTitle("", false, "");
    };
  }, []);

  useEffect(() => {
    if (table.data !== -1 && table.nextToken === null) {
      let _chartData = [];
      for (let data of table.data) {
        let lines = ["sbp", "dbp", "heartrate", "height", "weight"];
        for (let line of lines) {
          _chartData.push({
            type: line,
            value: data[line],
            date: moment.utc(data.date).local().format("YYYY/MM/DD"),
            deleted: data["_deleted"] || data["deleted"],
          });
        }
      }

      // console.log(table.data);
      setDisplayList(table.data.sort((a, b) => (a.date > b.date ? -1 : 1)));
      setChartData(_chartData);
      setTable({
        ...table,
        loading: false,
      });
    }
  }, [table.data, table.nextToken]);

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  async function createHealthRecords(data) {
    try {
      const chunkSize = 100;
      const chunks = [];
      let failedRecords = [];
      
      // Split data into chunks of 100
      for (let i = 0; i < data.length; i += chunkSize) {
        chunks.push(data.slice(i, i + chunkSize));
      }

      let totalImported = 0;
      
      // Process each chunk with a delay between chunks
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        try {
          const { document, variables } = combineQuery('CreateHealthRecord')
            .addN(gql`${createHealthRecord}`, chunk.map(item => ({ input: item })));

          notification.info({
            message: "Processing records",
            description: `Processing chunk ${i + 1} of ${chunks.length} (${chunk.length} records)`
          });

          const _result = await API.graphql({
            query: print(document),
            variables,
            authMode: "AMAZON_COGNITO_USER_POOLS",
          });

          totalImported += chunk.length;

          // Add a 1-second delay between chunks to avoid rate limiting
          if (i < chunks.length - 1) {
            await delay(1000);
          }
        } catch (chunkError) {
          console.error("Chunk error:", chunkError);
          failedRecords = [...failedRecords, ...chunk];
          notification.warning({
            message: "Chunk import failed",
            description: `Failed to import chunk ${i + 1}. These records will be skipped.`
          });
          
          // Add a longer delay after a failed chunk
          await delay(2000);
        }
      }

      if (totalImported === 0) {
        notification.error({
          message: "Import failed",
          description: "Failed to import any records"
        });
      } else {
        notification.success({
          message: "Records imported",
          description: `Successfully imported ${totalImported} records${failedRecords.length ? `. Failed to import ${failedRecords.length} records.` : ''}`
        });
        getHealthRecords();
      }
    } catch (error) {
      console.error("error:", error);
      notification.error({
        message: "Error importing records",
        description: "An error occurred while importing records"
      });
    } finally {
      setUploadedObj(null);
    }
  }

  useEffect(() => {
    if (uploadedObj) {
      let keys = [
        'id',
        'accountID',
        'date',
        'name',
        'heartrate',
        'sbp',
        'dbp',
        'height',
        'weight',
        'createdOn',
        'updatedOn',
        'deleted',
        'deletedOn',
      ];
      let data = uploadedObj
        .filter(item => keys.every(key => item.hasOwnProperty(key)))
        .map(item => {
          return keys.reduce((acc, key) => {
            acc[key] = item[key] || null;
            return acc;
          }, {});
        });

      // Filter out records that already exist in table.data
      const existingIds = new Set(table.data.map(record => record.id));
      const newData = data.filter(record => !existingIds.has(record.id));

      if (newData.length === 0) {
        notification.info({
          message: "No new records",
          description: "All records already exist in the system"
        });
        setUploadedObj(null);
        return;
      }

      notification.info({
        message: "Processing records",
        description: `Found ${data.length} records, ${newData.length} new records will be imported`
      });

      createHealthRecords(newData);
    }
  }, [uploadedObj])

  const columns = [
    {
      title: "accountID",
      dataIndex: "accountID",
      width: 150,
      render: (value) => {
        return accountID ? user?.name : value;
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      width: 200,
      render: (value, _, _index) => {
        return moment.utc(value).local().format("DD MMM YYYY, HH:mm:ss");
      },
    },
    {
      title: "id",
      dataIndex: "id",
      width: 300,
    },
    {
      title: "SBP",
      dataIndex: "sbp",
      width: 100,
    },
    {
      title: "DBP",
      dataIndex: "dbp",
      width: 100,
    },
    {
      title: "Heart Rate",
      dataIndex: "heartrate",
      width: 100,
    },
    {
      title: "Height (cm)",
      dataIndex: "height",
      width: 100,
    },
    {
      title: "Weight (KG)",
      dataIndex: "weight",
      width: 100,
    },
    {
      title: "CreatedOn",
      dataIndex: "createdOn",
      width: 200,
      render: (value, _, _index) => {
        return moment.utc(value).local().format("DD MMM YYYY, HH:mm:ss");
      },
    },
    {
      title: "UpdatedOn",
      dataIndex: "updatedOn",
      width: 200,
      render: (value, _, _index) => {
        return moment.utc(value).local().format("DD MMM YYYY, HH:mm:ss");
      },
    },
    {
      title: "Deleted",
      dataIndex: "deleted",
      width: 100,
    },
    {
      title: "DeletedOn",
      dataIndex: "deletedOn",
      width: 200,
      render: (value, _, _index) => {
        return (
          value && moment.utc(value).local().format("DD MMM YYYY, HH:mm:ss")
        );
      },
    },
  ];

  const handleTableChange = (_paginate) => {
    getHealthRecords(
      {
        //   ...table.pagination,
        //   current: paginate.current,
        //   pageSize: paginate.pageSize,
        //   offset: paginate.current * paginate.pageSize - paginate.pageSize,
      },
      values
    );
  };

  function processJsonData(jsonData) {
    return jsonData.map(row => {
      return {
        id: row.id || `hr-${row.date}-${Math.random().toString(36).substr(2, 9)}`,
        __typename: "HealthRecord",
        accountID: row.accountID,
        date: row.date,
        sbp: row.sbp || 0,
        dbp: row.dbp || 0,
        heartrate: row.heartrate || 0,
        height: row.height || 0,
        weight: row.weight || 0,
        name: row.name,
        createdOn: row.createdOn || new Date().toISOString(),
        updatedOn: row.updatedOn || new Date().toISOString(),
        deleted: row.deleted || row._deleted || false,
        deletedOn: row.deletedOn || null,
        _version: row._version || 1,
        _lastChangedAt: row._lastChangedAt || Date.now()
      };
    });
  }

  function handleUpload(item) {
    const file = item.file.originFileObj;
    if (file.type !== "application/json") {
      notification.error({
        message: "Invalid file type",
        description: "Please upload a JSON file"
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result);
        if (!Array.isArray(jsonData)) {
          notification.error({
            message: "Invalid JSON format",
            description: "File must contain an array of health records"
          });
          return;
        }

        const processedData = processJsonData(jsonData);
        if (processedData.length === 0) {
          notification.warning({
            message: "No valid records",
            description: "No valid health records found in JSON"
          });
          return;
        }

        setUploadedObj(processedData);
      } catch (error) {
        notification.error({
          message: "JSON Parse Error",
          description: error.message
        });
      }
    };

    reader.onerror = (error) => {
      notification.error({
        message: "File Read Error",
        description: error.message
      });
    };

    reader.readAsText(file);
  }

  const vc = useMemo(() => {
    return VList({
      height: 500,
      onReachEnd: table.nextToken?.length
        ? getHealthRecords(table.nextToken)
        : null,
    });
  }, [getHealthRecords, table.nextToken]);

  const config = {
    data: chartData
      .filter((s) => !s.deleted)
      .sort((a, b) => (a.date > b.date ? 1 : -1)),
    xField: "date",
    yField: "value",
    seriesField: "type",
    xAxis: {
      title: {
        text: "Date",
        style: {
          fontSize: 16,
        },
      },
    },
    yAxis: {
      title: {
        text: "Value",
        style: {
          fontSize: 16,
        },
      },
      label: {
        // 数值格式化为千分位
        formatter: (v) =>
          `${v}`.replace(/\d{1,3}(?=(\d{3})+$)/g, (s) => `${s},`),
      },
    },
    color: ({ type }) => {
      switch (type) {
        case "sbp":
          return "#F4664A";
        case "dbp":
          return "#30BF78";
        case "heartrate":
          return "#FAAD14";
        default:
          return `#${Math.floor(Math.random() * 16777215).toString(16)}`;
      }
    },
    lineStyle: ({ _type }) => {
      return {
        opacity: 1,
      };
    },
  };

  return (
    <div className="container-content allow-overflow">
      <div className="content-content">
        <Descriptions
          title={
            <div>
              <span></span>
              <Tag>Health Records</Tag>
              {accountID ? <Tag>{user?.name}</Tag> : <></>}
            </div>
          }
          extra={!accountID && displayList.length && table.nextToken === null ? <Upload
            accept=".json"
            showUploadList={false}
            onChange={handleUpload}
          >
            <Button icon={<UploadOutlined />}>Import JSON</Button>
          </Upload> : <></>}
        />
      </div>
      <Spin spinning={table.loading}>
        <div className="content-content">
          {viewChart && displayList.length ? (
            <Line style={{ margin: 8 }} {...config} />
          ) : (
            <></>
          )}
          {displayList.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <Table
              columns={columns}
              rowKey={(record) => record.id}
              dataSource={displayList}
              pagination={false}
              loading={table.loading}
              scroll={{ y: 500 }}
              onChange={handleTableChange}
              components={vc}
              rowClassName={"table-row-clickable"}
            />
          )}
        </div>
      </Spin>
    </div>
  );
};

export default HealthRecords;
